import type { ThemeConfig, ResolvedThemeSettings } from '../types/theme-config';
import { get } from '../core/client';
import { buildEndpoint } from '../core/endpoint';

type JsonRecord = Record<string, unknown>;

export interface PublishedBuilderSettings {
  version: number;
  revision: number;
  theme: {
    content: JsonRecord;
    layout: JsonRecord;
    style_slots: JsonRecord;
    pages?: unknown[];
    terms?: JsonRecord;
    [key: string]: unknown;
  };
  [key: string]: unknown;
}

export interface PublishedThemeSettingsPayload {
  settings: ResolvedThemeSettings;
  builder_settings: PublishedBuilderSettings | null;
  content: JsonRecord;
  style_slots: JsonRecord;
}

function isRecord(value: unknown): value is JsonRecord {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function normalizePublishedBuilderSettings(value: unknown): PublishedBuilderSettings | null {
  if (!isRecord(value) || !isRecord(value.theme)) {
    return null;
  }

  return {
    ...value,
    version: typeof value.version === 'number' ? value.version : 0,
    revision: typeof value.revision === 'number' ? value.revision : 0,
    theme: {
      ...value.theme,
      content: isRecord(value.theme.content) ? value.theme.content : {},
      layout: isRecord(value.theme.layout) ? value.theme.layout : {},
      style_slots: isRecord(value.theme.style_slots) ? value.theme.style_slots : {},
      ...(Array.isArray(value.theme.pages) ? { pages: value.theme.pages } : {}),
      ...(isRecord(value.theme.terms) ? { terms: value.theme.terms } : {}),
    },
  };
}

function normalizePublishedThemeSettingsPayload(value: unknown): PublishedThemeSettingsPayload | null {
  if (!isRecord(value)) {
    return null;
  }

  return {
    settings: isRecord(value.settings) ? value.settings as ResolvedThemeSettings : {},
    builder_settings: normalizePublishedBuilderSettings(value.builder_settings),
    content: isRecord(value.content) ? value.content : {},
    style_slots: isRecord(value.style_slots) ? value.style_slots : {},
  };
}

export async function fetchPublishedBuilderSettings(
  shopSlug: string
): Promise<PublishedThemeSettingsPayload | null> {
  const result = await get<PublishedThemeSettingsPayload>(
    buildEndpoint('/v1/storefront/themes/builder/published/:shopSlug', { shopSlug })
  );

  return result.success && result.data ? normalizePublishedThemeSettingsPayload(result.data) : null;
}

export async function fetchPublishedThemeSettings(
  shopSlug: string
): Promise<ResolvedThemeSettings | null> {
  const payload = await fetchPublishedBuilderSettings(shopSlug);
  return payload ? payload.settings : null;
}

export function resolveDefaults(config: ThemeConfig): ResolvedThemeSettings {
  const resolved: ResolvedThemeSettings = {};
  for (const [category, fields] of Object.entries(config.settings)) {
    resolved[category] = {};
    for (const [key, field] of Object.entries(fields)) {
      resolved[category][key] = field.default;
    }
  }
  return resolved;
}

export function mergeSettings(
  defaults: ResolvedThemeSettings,
  overrides: Partial<ResolvedThemeSettings>
): ResolvedThemeSettings {
  const merged = { ...defaults };
  for (const [category, fields] of Object.entries(overrides)) {
    if (fields) {
      merged[category] = { ...merged[category], ...fields };
    }
  }
  return merged;
}
